import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabase/client';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'super' | 'normal';
  createdAt: string;
}

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  admins: AdminUser[];
  addAdmin: (user: Omit<AdminUser, 'id' | 'createdAt'>, password: string) => Promise<void>;
  removeAdmin: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 管理员邮箱后缀
const ADMIN_EMAIL_SUFFIX = '@admin.local';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [admins, setAdmins] = useState<AdminUser[]>([]);

  useEffect(() => {
    loadAdmins();

    // 订阅认证状态变化，让管理员端能感知 Token 刷新失败、登出等事件
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // 仅处理管理员账号（邮箱后缀为 @admin.local）
      const email = session?.user?.email || '';
      const isAdminAccount = email.endsWith(ADMIN_EMAIL_SUFFIX);

      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESH_FAILURE' && !session)) {
        // 用户主动登出，或 Token 刷新失败导致 session 丢失
        // 注意：TOKEN_REFRESH_FAILURE 时 session 可能为 null，这里直接清空管理员状态
        // Header 中的重试机制会处理普通用户的容错，管理员端直接清空避免权限错乱
        if (event === 'SIGNED_OUT') {
          setUser(null);
        }
        // TOKEN_REFRESH_FAILURE 不立即清空，等待 Header 重试流程
        // 如果重试也失败，SIGNED_OUT 事件会触发清空
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (session?.user && isAdminAccount) {
          // 恢复管理员状态：从 admin_users 表查询完整信息
          restoreAdminUser(session.user.id, session.user.email || '');
        } else if (session?.user && !isAdminAccount) {
          // 普通用户登录，清空管理员状态（避免角色混淆）
          setUser(null);
        }
      }
    });

    // 初始化时尝试恢复登录状态
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const email = session.user.email || '';
        if (email.endsWith(ADMIN_EMAIL_SUFFIX)) {
          restoreAdminUser(session.user.id, email);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // 从 admin_users 表恢复管理员用户信息
  const restoreAdminUser = async (authUserId: string, email: string) => {
    try {
      // 从邮箱提取用户名（admin.local 后缀）
      const username = email.replace(ADMIN_EMAIL_SUFFIX, '');

      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('username', username)
        .eq('status', 'active')
        .maybeSingle();

      if (error || !data) {
        // admin_users 表中找不到对应记录，可能是数据不一致，登出
        console.warn('[AdminAuth] 未找到管理员记录，可能数据不一致:', username);
        setUser(null);
        return;
      }

      setUser({
        id: data.id,
        username: data.username,
        email: data.email || '',
        role: data.role === 'super' ? 'super' : 'normal',
        createdAt: data.created_at
      });
    } catch (err) {
      console.error('[AdminAuth] 恢复管理员状态失败:', err);
      setUser(null);
    }
  };

  const loadAdmins = async () => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setAdmins((data as Array<{ id: string; username: string; email?: string; role: string; created_at: string }>).map(a => ({
        id: a.id,
        username: a.username,
        email: a.email || '',
        role: a.role === 'super' ? 'super' : 'normal',
        createdAt: a.created_at
      })));
    }
  };

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .eq('username', username)
      .eq('status', 'active')
      .maybeSingle();

    if (error || !data) return false;

    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: `${username}@admin.local`,
      password
    });

    if (authError || !authData.user) return false;

    setUser({
      id: (data as { id: string; username: string; email?: string; role: string; created_at: string }).id,
      username: (data as { id: string; username: string; email?: string; role: string; created_at: string }).username,
      email: (data as { id: string; username: string; email?: string; role: string; created_at: string }).email || '',
      role: (data as { id: string; username: string; email?: string; role: string; created_at: string }).role === 'super' ? 'super' : 'normal',
      createdAt: (data as { id: string; username: string; email?: string; role: string; created_at: string }).created_at
    });
    return true;
  }, []);

  const logout = useCallback(() => {
    supabase.auth.signOut();
    setUser(null);
  }, []);

  const addAdmin = useCallback(async (newUser: Omit<AdminUser, 'id' | 'createdAt'>, password: string) => {
    const { error: authError } = await supabase.auth.signUp({
      email: `${newUser.username}@admin.local`,
      password,
      options: { data: { username: newUser.username } }
    });

    if (authError) throw authError;

    const { error } = await supabase.from('admin_users').insert({
      username: newUser.username,
      email: newUser.email,
      phone: '',
      role: newUser.role,
      status: 'active'
    });

    if (error) throw error;
    await loadAdmins();
  }, []);

  const removeAdmin = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('admin_users')
      .delete()
      .eq('id', id)
      .neq('role', 'super');

    if (error) throw error;
    await loadAdmins();
  }, []);

  const isSuperAdmin = user?.role === 'super';

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isSuperAdmin,
      login,
      logout,
      admins,
      addAdmin,
      removeAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
